import React, { useState } from 'react';
import {
  Alert, Avatar, Box, Button, Tooltip, Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { normalizeAvatarUrl } from '../../../../../utils/avatar';

// Presentation only. Finance and Sales Order use the same authoritative,
// read-only server pre-check and manual notification endpoint. This component
// intentionally does not change invoice or customer-email eligibility.
const fieldIcon = (label) => String(label || '').toLowerCase().includes('address')
  ? <PlaceOutlinedIcon sx={{ color: '#AE6417', fontSize: 21 }} />
  : <PersonOutlineIcon sx={{ color: '#AE6417', fontSize: 21 }} />;

const getInitials = (name) => String(name || '').trim()
  .split(/\s+/).filter(Boolean).slice(0, 2)
  .map((part) => part[0].toUpperCase()).join('') || '?';

const FieldCard = ({ field }) => (
  <Box
    sx={{
      display: 'flex', alignItems: 'center', gap: 1,
      p: 1.25, borderRadius: 2, minWidth: 0, bgcolor: '#FFF',
      border: field.complete ? '1px solid #D7E8DC' : '1px solid #EED4B9',
    }}
  >
    <Box sx={{ flexShrink: 0, display: 'flex' }}>
      {field.complete ? <CheckCircleOutlineIcon sx={{ color: '#198047', fontSize: 21 }} /> : fieldIcon(field.label)}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 750, lineHeight: 1.4 }}>
        {field.label}
      </Typography>
      <Typography
        sx={{
          fontSize: 11.5, color: field.complete ? '#266E44' : 'text.secondary',
          mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={field.complete ? (field.preview || 'Added') : 'Not added'}
      >
        {field.complete ? (field.preview || 'Added') : 'Not added'}
      </Typography>
    </Box>
  </Box>
);

const AssignedRecipient = ({ recipient }) => {
  const name = recipient.name || recipient.email || 'Assigned project member';
  const role = String(recipient.role || '').replace(/-/g, ' ');
  return (
    <Tooltip arrow title={[name, role, recipient.email].filter(Boolean).join(' · ')}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.15, p: 1.2,
        border: '1px solid #DEE5ED', borderRadius: 2, bgcolor: '#FFF',
        minWidth: 0,
      }}>
        <Avatar
          src={normalizeAvatarUrl(recipient.profilePicture) || undefined}
          alt={name}
          sx={{
            width: 40, height: 40, flexShrink: 0,
            bgcolor: '#D8F1E0', color: '#166534', fontSize: 13, fontWeight: 750,
          }}
        >
          {getInitials(name)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 750, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {name}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', textTransform: 'capitalize' }}>
            {role}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {recipient.email}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

export default function SalesOrderInvoiceAddressPrecheck({
  precheck,
  loading = false,
  error = '',
  sending = false,
  sent = false,
  disabled = false,
  sendInvoiceEmailNow = false,
  onNotify,
  onRefresh,
}) {
  const [showCompletedDetails, setShowCompletedDetails] = useState(false);
  const [showMissingDetails, setShowMissingDetails] = useState(false);
  if (loading) {
    return <Typography variant="body2" color="text.secondary">Rechecking Sales Order addresses…</Typography>;
  }

  if (error || !precheck) {
    return (
      <Alert severity={sendInvoiceEmailNow ? 'warning' : 'info'} sx={{ borderRadius: 2 }} data-testid="so-invoice-address-precheck-unavailable">
        <Typography variant="body2">{error || 'Sales Order address details are not available for this invoice.'}</Typography>
        {sendInvoiceEmailNow ? (
          <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700 }}>
            Address details could not be verified. Creating and sending now may email an invoice with incomplete addresses.
          </Typography>
        ) : null}
        <Button size="small" onClick={onRefresh} disabled={disabled} startIcon={<RefreshIcon />} sx={{ mt: 0.5, textTransform: 'none' }}>
          Retry check
        </Button>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
          This check is informational and does not prevent invoice creation.
        </Typography>
      </Alert>
    );
  }

  const missing = Array.isArray(precheck.missing) ? precheck.missing : [];
  const fields = Array.isArray(precheck.fields) ? precheck.fields : [];
  const recipients = Array.isArray(precheck.recipients) ? precheck.recipients : [];
  const ready = precheck.ready === true;
  const displayFields = fields.length ? fields : missing.map((label) => ({ label, complete: false }));
  if (ready) {
    return (
      <Box data-testid="so-invoice-address-precheck" sx={{
        border: '1px solid #D5E9DB', bgcolor: '#F7FCF8',
        borderRadius: 2, px: 1.5, py: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <CheckCircleOutlineIcon sx={{ color: '#17803D', fontSize: 20 }} />
          <Box sx={{ flex: 1, minWidth: 140 }}>
            <Typography variant="body2" sx={{ fontWeight: 750 }}>
              Sales Order details complete
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Billing and shipping contacts and addresses are saved.
            </Typography>
          </Box>
          <Button size="small" variant="text" disabled={disabled}
            onClick={() => setShowCompletedDetails((shown) => !shown)}
            sx={{ textTransform: 'none', minWidth: 0 }}>
            {showCompletedDetails ? 'Hide details' : 'View details'}
          </Button>
          <Tooltip title="Check for recent address changes">
            <span>
              <Button size="small" disabled={disabled} onClick={onRefresh}
                sx={{ textTransform: 'none', minWidth: 0 }}
                startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}>Recheck</Button>
            </span>
          </Tooltip>
        </Box>
        {showCompletedDetails && displayFields.length > 0 ? (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
            gap: 0.75, mt: 1.25,
          }}>
            {displayFields.map((field, index) => (
              <FieldCard key={field.key || field.label || index} field={field} />
            ))}
          </Box>
        ) : null}
      </Box>
    );
  }
  return (
    <Box data-testid="so-invoice-address-precheck"
      sx={{ px: 1.5, py: 1, borderRadius: 2, border: '1px solid #F0C28F', bgcolor: '#FFFBF6' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <WarningAmberOutlinedIcon sx={{ color: '#A84B08', fontSize: 20 }} />
        <Box sx={{ minWidth: 150, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 750 }}>
            Sales Order details needed · {missing.length} missing
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {missing.join(', ') || 'Billing or shipping details are incomplete'}
          </Typography>
        </Box>
        <Button size="small" disabled={disabled} onClick={() => setShowMissingDetails((shown) => !shown)}
          aria-expanded={showMissingDetails} sx={{ textTransform: 'none', minWidth: 0 }}>
          {showMissingDetails ? 'Hide details' : 'View details'}
        </Button>
        <Button size="small" disabled={disabled} onClick={onRefresh}
          startIcon={<RefreshIcon sx={{ fontSize: 16 }} />} sx={{ textTransform: 'none', minWidth: 0 }}>
          Recheck
        </Button>
      </Box>
      {sendInvoiceEmailNow && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Sending now may email an invoice with incomplete addresses. Turn off email sending to create a draft only.
        </Alert>
      )}
      {showMissingDetails && (
        <>
      {displayFields.length > 0 && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
          gap: 1, mt: 2,
        }}>
          {displayFields.map((field, index) => <FieldCard key={field.key || field.label || index} field={field} />)}
        </Box>
      )}

      {!ready && (
        <>
          {sendInvoiceEmailNow && (
            <Alert severity="warning" sx={{ mt: 1.75, borderRadius: 2 }} data-testid="so-invoice-send-now-warning">
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Send invoice email now is enabled.
              </Typography>
              <Typography variant="body2">
                Continuing may email the customer an invoice with missing billing or shipping details.
                Turn off sending to create the invoice without emailing it.
              </Typography>
            </Alert>
          )}

          <Box sx={{ mt: 2, pt: 1.75, borderTop: '1px solid #E8E4DE' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Project team to notify
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3, fontSize: 12.5 }}>
              Only eligible users assigned to this project can receive the reminder.
              It is sent only when you click Notify assigned reps.
            </Typography>
            {recipients.length > 0 ? (
              <Box sx={{
                display: 'grid', gap: 1, mt: 1.4,
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
              }}>
                {recipients.map((person) => <AssignedRecipient key={person.id || person.email} recipient={person} />)}
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.1 }}>
                No eligible order rep, client rep or administrator with an email address is assigned.
              </Typography>
            )}
          </Box>
        </>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.15, mt: 1.75, alignItems: 'center' }}>
        {!ready && recipients.length > 0 && (
          <Button
            onClick={onNotify}
            disabled={disabled || sending || sent}
            variant="contained"
            startIcon={sent ? <CheckCircleOutlineIcon /> : <MailOutlineIcon />}
            sx={{
              bgcolor: '#D71920', '&:hover': { bgcolor: '#B7131C' },
              boxShadow: 'none', textTransform: 'none', borderRadius: 2,
              px: 2, minHeight: 40, fontWeight: 750,
            }}
          >
            {sent ? 'Reminder sent' : sending ? 'Sending…' : 'Notify assigned reps'}
          </Button>
        )}
        <Button
          onClick={onRefresh}
          disabled={disabled}
          variant="outlined"
          startIcon={<RefreshIcon />}
          sx={{
            textTransform: 'none', borderRadius: 2, minHeight: 40,
            borderColor: '#CCD6E2', color: 'text.primary', px: 1.75,
          }}
        >
          Recheck details
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Missing addresses do not block creating a draft invoice.
      </Typography>
        </>
      )}
    </Box>
  );
}
